import {clone} from 'lodash'
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Holidays from 'date-holidays';
const hd = new Holidays("US");
import { SIGOPS_MARK, SAFETY_MARK } from '@shared/constants'

dayjs.extend(utc);
dayjs.extend(timezone);

export const delay = (t) => new Promise((resolve) => setTimeout(resolve, t));

export function updateFileName(fileName) {
    if (fileName.length > 15) {
        const first15Chars = fileName.slice(0, 15);
        const last15Chars = fileName.slice(-15);
        const updatedFileName = first15Chars + " ..." + last15Chars;
        return updatedFileName;
    }

    return fileName; // return the original fileName if it's less than or equal to 15 characters
}

export function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["b", "kb", "mb", "gb"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return {
        size: parseFloat((bytes / Math.pow(k, i)).toFixed(dm)),
        unit: sizes[i],
    };
}

export const getFileName = (fileName) => fileName.split("/")?.pop() || "";

export function getDatesInRange(dateRange, options) {
    const format = "YYYY-MM-DD";
    let dateRangeArray = [];
    let currentDate = dayjs(dateRange.start_date);
    const endDate = dayjs(dateRange.end_date);

    while (
        currentDate.isBefore(endDate) ||
        currentDate.isSame(endDate, "day")
    ) {
		if (options?.exclude_holidays && hd.isHoliday(currentDate)?.[0]?.type === "public") {
			currentDate = currentDate.add(1, "day");
		}
		else {
			dateRangeArray.push(currentDate.format(format));
			currentDate = currentDate.add(1, "day");
		}
    }

    return dateRangeArray;
}

export function getHolidaysInRange(dateRange) {
    const format = "YYYY-MM-DD";
    let dateRangeArray = [];
    let currentDate = dayjs(dateRange.start_date);
    const endDate = dayjs(dateRange.end_date);

    while (
        currentDate.isBefore(endDate) ||
        currentDate.isSame(endDate, "day")
    ) {
		if (hd.isHoliday(currentDate)?.[0]?.type === "public") {
			dateRangeArray.push(currentDate.format(format));
			currentDate = currentDate.add(1, "day");
		}
    }

    return dateRangeArray;
}

export function removeDuplicatesDate(date_range, excluded_dates) {
    // Convert all dates in list B to dayjs objects for comparison
    const formattedExcludedDates = excluded_dates.map((date) => dayjs(date));

    // Filter out dates in list A if they're found in list B
    const filteredListDateRange = date_range.filter((dateRange) => {
        const dayjsDateRange = dayjs(dateRange);
        return !formattedExcludedDates.some((excludedDate) => excludedDate.isSame(dayjsDateRange));
    });

    return filteredListDateRange;
}

export const convertDatesToDateRanges = (dates, options) => {
	// Assumes dates are strings of 'YYYY-MM-DD'
	// Date ranges are { start_date: 'YYYY-MM-DD', end_date: 'YYYY-MM-DD' }

	if (!dates?.length) return []

	const deduped_dates = Array.from(new Set(dates))

	const sorted_dates = deduped_dates.toSorted((a, b) => {
		const date_a = dayjs(a)
		const date_b = dayjs(b)
		return (date_a.isBefore(date_b) || date_a.isSame(date_b)) ? -1 : 1
	})


	let date_ranges = []
	let current_date_range = {}
	let previous_date = dayjs(sorted_dates[0])

	current_date_range.start_date = previous_date.format('YYYY-MM-DD')
	current_date_range.end_date = previous_date.format('YYYY-MM-DD')

	for (let i = 1; i < sorted_dates.length; i++) {

		const date = dayjs(sorted_dates[i])

		if (!date.isSame(previous_date.add(1, 'day'))) {

			current_date_range.end_date = previous_date.format('YYYY-MM-DD')

			date_ranges.push(clone(current_date_range))

			current_date_range.start_date = date.format('YYYY-MM-DD')
			current_date_range.end_date = date.format('YYYY-MM-DD')

		}

		if (i === sorted_dates.length - 1) {
			current_date_range.end_date = date.format('YYYY-MM-DD')
		}

		previous_date = date

	}

	date_ranges.push(clone(current_date_range))


	return date_ranges

}

export const convertDateRangesToDates = (date_ranges, options) => {
	// Assumes dates are strings of 'YYYY-MM-DD'
	// Date ranges are { start_date: 'YYYY-MM-DD', end_date: 'YYYY-MM-DD' }

	if (!date_ranges?.length) return []

	const raw_dates = date_ranges.reduce((dates, current_date_range) => dates.concat(getDatesInRange(current_date_range, {exclude_holidays: options?.exclude_holidays})), [])

	const deduped_dates = Array.from(new Set(raw_dates))

	const dates = (options?.not_sorted) ? deduped_dates
	: deduped_dates.toSorted((a, b) => {
		const date_a = dayjs(a)
		const date_b = dayjs(b)
		return (date_a.isBefore(date_b) || date_a.isSame(date_b)) ? -1 : 1
	})


	return dates

}

export const timeAgo = (date) => {
    if(!date) return ""
    const currentTime = new Date();
    const datePast = new Date(date);
    const timeDifference = currentTime.getTime() - datePast.getTime();

    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);

    // Calculate months difference
    const months =
        currentTime.getMonth() -
        datePast.getMonth() +
        12 * (currentTime.getFullYear() - datePast.getFullYear());

    if (seconds < 60) {
        return "Just now";
    } else if (minutes === 1) {
        return "1 min ago";
    } else if (minutes < 60) {
        return `${minutes} mins ago`;
    } else if (hours === 1) {
        return "1 hour ago";
    } else if (hours < 24) {
        return `${hours} hours ago`;
    } else if (days === 1) {
        return "Yesterday";
    } else if (days < 7) {
        return `${days} days ago`;
    } else if (weeks === 1) {
        return "1 week ago";
    } else if (weeks < 5) {
        return `${weeks} weeks ago`;
    } else if (months === 1) {
        return "1 month ago";
    } else if (months < 12) {
        return `${months} months ago`;
    } else {
        return datePast.toLocaleDateString();
    }
};

export const isValidEmail = (email) => {
    let regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return regex.test(email);
};

export const  mergeAlerts = (network_metric_alerts, network_baseline_alerts) => {
    const mergedAlerts = {};

    // Get all unique keys from both objects
    const allKeys = new Set([
        ...Object.keys(network_metric_alerts),
        ...Object.keys(network_baseline_alerts)
    ]);

    // Iterate over each key and apply the specified logic
    allKeys.forEach(key => {
        const metricAlert = network_metric_alerts[key];
        const baselineAlert = network_baseline_alerts[key];

        if (metricAlert || baselineAlert) {
            mergedAlerts[key] = metricAlert || baselineAlert;
        } else {
            mergedAlerts[key] = 0;
        }
    });

    return mergedAlerts;
}

/**
 * BEGIN Reference: https://stackoverflow.com/a/21015393
 * USAGE:
 * 		getTextWidth("hello there!", "bold 12pt arial");  // close to 86
 * 		const fontSize = getTextWidth(text, getCanvasFont(myEl));
 *
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text, font) {
	// re-use canvas object for better performance
	const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
	const context = canvas.getContext("2d");
	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
}

export function getCssStyle(element, prop) {
	return window.getComputedStyle(element, null).getPropertyValue(prop);
}

export function getCanvasFont(el = document.body) {
	const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
	const fontSize = getCssStyle(el, 'font-size') || '16px';
	const fontFamily = getCssStyle(el, 'font-family') || 'Times New Roman';

	return `${fontWeight} ${fontSize} ${fontFamily}`;
}

/**
 * END Reference: https://stackoverflow.com/a/21015393
 */


export const cleanedStr = (text) => text.replace(/[!@#$%^&*(){}\[\]<>=+;:'",./?\\|~\-_]/g, ' ').toLowerCase();

export const getUniqueItemsByProperty = (items, property) => {
    const seenValues = new Set();

    return items?.filter((item) => {
        const value = item[property] || item[property];
        if (seenValues.has(value)) {
            return false;
        } else {
            seenValues.add(value);
            return true;
        }
    });
};

export const getMatchWithRegex = (text, regex) => text.match(regex);

const isElementInParentViewport = (child, parent) => {
    const childRect = child.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    return (
        childRect.top >= parentRect.top &&
        childRect.left >= parentRect.left &&
        childRect.bottom <= parentRect.bottom &&
        childRect.right <= parentRect.right
    );
};

export const scrollToElementIfNotInParentView = (childId, parentId) => {
    const child = document.getElementById(childId);
    const parent = document.getElementById(parentId);

    if (child && parent && !isElementInParentViewport(child, parent)) {
        parent.scrollTop = child.offsetTop - parent.offsetTop;
        // Optionally, add smooth scrolling
        parent.scrollTo({ top: child.offsetTop - parent.offsetTop, behavior: 'smooth' });
    }
};


export const checkWordsInString = (mainString, wordsToCheck) => {
   // Split the string wordsToCheck into words based on spaces
   const words = cleanedStr(wordsToCheck).split(' ');

   // Check if every word in wordsToCheck is included in mainString
   return words.every(word => cleanedStr(mainString).includes(word));
};

export const isValidTime = (timeStr) => {
    // Parse the time string with the format 'HH:mm'
    const isValid = dayjs(timeStr, 'YYYY-MM-DD', true).isValid();
    return isValid;
};


export const getAverageValue = (value1, value2, average1, average2) => {
    if (average1 === 0 && average2 === 0) return 0;

    const num1 = value1 * average1;
    const num2 = value2 * average2;
    const denominator = average1 + average2;
    const result = (num1 + num2) / denominator;
    return result;
};

export const getMarkLevel = (current_route) => {
    if (current_route?.key?.includes('sigops')) {
        return SIGOPS_MARK;
    }
    return SAFETY_MARK;
};
export const distinctArrayByFields = (array, fields) => {
    const seen = new Set();
    return array?.filter(item => {
        const key = fields?.map(field => item[field]).join('|');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};


const getFirstMonday = (date) => {
    let firstMonday = date.startOf('month');
    while (firstMonday.day() !== 1) {
        firstMonday = firstMonday.add(1, 'day');
    }
    return firstMonday;
};


export const getFrequencyDefault = (timeZone = dayjs.tz.guess()) => {
    const sendAtUtc = dayjs.utc('17:00:00', 'HH:mm:ss');
    const sendAtLocal = sendAtUtc.tz(timeZone);
    let dayOfWeek = 1; // Default day of week

    if (sendAtLocal.date() > sendAtUtc.date()) {
        dayOfWeek = 2; // Move to next day if it shifted
    }

    return {
        send_at: sendAtLocal.format('HH:mm:ss'),
        send_on: {
            date_of_year: null,
            day_of_weeks: [dayOfWeek],
            week_of_month: null,
            week_of_quarter: null,
        },
        repeat_type: 'weekly',
    };
};

export const sortAtomicMovementsData = (data, nameKey = "name") => {
    const directionOrder = { "SB": 0, "WB": 1, "NB": 2, "EB": 3 };
    const movementOrder = { "Left": 0, "Through": 1, "Thru": 1, "Right": 2 };

    const sortKey = (d) => {
        const parts = d[nameKey].split(" - ");
        const direction = parts[0].trim();
        const movement = parts[1]?.trim();

        return [
            directionOrder[direction] ?? 99,
            movementOrder[movement] ?? 99,
        ];
    };

    return data.sort((a, b) => {
        const keyA = sortKey(a);
        const keyB = sortKey(b);

        for (let i = 0; i < keyA.length; i++) {
            if (keyA[i] !== keyB[i]) return keyA[i] - keyB[i];
        }

        return 0;
    });
};


export const sortArrayByReference = (array1, array2, id = "id") => {
    const idOrder = new Map(array2.map((item, index) => [item[id], index]));
    return array1.sort((a, b) => (idOrder.get(a[id]) ?? Infinity) - (idOrder.get(b[id]) ?? Infinity));
};

export const groupByKeysWithCustomItems = (array, keys, itemsKey = "items") =>
    Object.values(array.reduce((result, item) => {
      const groupKey = keys.map(k => item[k]).join("_");

      if (!result[groupKey]) {
        result[groupKey] = {
          ...Object.fromEntries(keys.map(k => [k, item[k]])),
          [itemsKey]: []
        };
      }

      const itemWithoutGroupKeys = { ...item };
      keys.forEach(k => delete itemWithoutGroupKeys[k]);

      result[groupKey][itemsKey].push(itemWithoutGroupKeys);
      return result;
    }, {}));

export const sortNullLast = (data, key, order = 'asc') => {
    if (!data) {
        return data
    }

    return data.sort((a, b) => {
        // equal items sort equally
        if (a[key] === b[key]) {
            return 0;
        }

        // nulls sort after anything else
        if (a[key] === null || a[key] === undefined) {
            return 1;
        }
        if (b[key] === null || b[key] === undefined) {
            return -1;
        }

        // otherwise, if we're ascending, lowest sorts first
        if (order === 'asc') {
            return a[key] < b[key] ? -1 : 1;
        }

        // if descending, highest sorts first
        return a[key] < b[key] ? 1 : -1;
    });
}

/**
 * Returns
 * (0) the smallest index `left` such that for all rows r and all j < left, r[j] is null. If no such index exists, returns 0.
 * (1) the smallest index `right` such that for all rows r and all j >= right, r[j] is null. If no such index exists, returns the array length.
 * ASSUMES ALL ROWS IN MATRIX HAVE THE SAME LENGTH!!!
*/
// export function getNonNullishBounds(matrix: (any | null | undefined)[][]): [number, number] {
export function getNonNullishBounds(matrix) {
	if (!matrix.length || !matrix[0].length) return [0, 0];

	const num_cols = matrix[0].length;

	let left = num_cols;
	let lastAllNonNullish = -1;

	for (const row of matrix) {

		// Find first non-nullish in this row
		let first = num_cols;
		for (let j = 0; j < num_cols; j++) {
			if (row[j] != null) {
				first = j;
				break;
			}
		}
		if (first < left) left = first;

		// Find last non-nullish in this row
		for (let j = num_cols - 1; j >= 0; j--) {
			if (row[j] != null) {
				if (j > lastAllNonNullish) lastAllNonNullish = j;
				break;
			}
		}
	}

	const right = lastAllNonNullish + 1;
	return [left, right];
}

// A failed metrics fetch can hand back an error payload where an array is expected; throwing inside
// a Mithril view blanks the whole panel, so coerce at the point of use.
export const asArray = (value) => (Array.isArray(value) ? value : []);
